// src/pages/TrackCreation/MuseumInfo.tsx
import React from "react";
import activeRadio from "../../../../../assets/images/radio_active.png";
import inactiveRadio from "../../../../../assets/images/radio_inactive.png";
import { useTranslation } from "react-i18next";
import { MuseumInfoProps } from "../../../../../types/screensTypes";
import { FileInput } from "../../../../../components/common/FileInput";
import RadioButton from "../../../../../components/common/RadioButton";
import TitleSubtitle from "../../../../../components/common/TitleSubtitle";
import TextInput from "../../../../../components/ui/TextInput";
import { useTrackCreation } from "../../../../../context/TrackCreationContext";
import { uploadFileToS3Complete } from "../../../../../api/s3";
import Button from "../../../../../components/ui/Button";
import ImageList from "../../../../../components/images/ImageList";
import ImageSpecsBadges from "../../../../../components/common/ImageSpecsBadges";
import { useImageValidation } from "../../../../../hooks/useImageValidation";
import { IMAGE_FORMAT, IMAGE_MAX_SIZE_200_KB } from "../../../../../utils/image";
import stepStyles from "../../createStep.module.css";
import styles from "./Information.module.css";

const MuseumInfo: React.FC<MuseumInfoProps> = ({ navigateToNext }) => {
    const { trackData, updateTrackData } = useTrackCreation();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const imageValidation = useImageValidation({
        type: "museumLogo",
        imageUrl: trackData.museumPictureUrl || undefined,
    });

    const handleFileSelected = async (files: File | File[]) => {
        const file = Array.isArray(files) ? files[0] : files; // Toujours prendre un seul fichier

        if (file) {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("No authentication token found");
                return;
            }

            await imageValidation.validateImage(file);

            uploadFileToS3Complete(
                {
                    file,
                    isPublic: true, // Public files for general uploads
                    isFolder: false, // No folder structure for general uploads
                },
                token,
            )
                .then((result: string) => {
                    updateTrackData("museumPictureUrl", result);
                })
                .catch((error) => {
                    console.log(error);
                });
        }
    };

    // Sets the museumPictureUrl from the ImageList component.
    // If pictures array is not empty, use the first picture; otherwise clear the value.
    const setPicturesFromList = (pictures: string[]): void => {
        if (pictures && pictures.length > 0) {
            updateTrackData("museumPictureUrl", pictures[0]);
        } else {
            updateTrackData("museumPictureUrl", null);
        }
    };

    return (
        <div className={`step-container ${stepStyles.stepContainer} ${stepStyles.step} ${stepStyles.stepMuseum}`}>
            <TitleSubtitle title={t("museum")} subtitle={t("fillTheMuseumSpecificInformation")} layout="museum" />

            <div className={styles.nameField}>
                <TextInput
                    placeholder={t("pleaseInsertTheMuseumName")}
                    value={trackData.museumName || ""}
                    onChange={(e) => updateTrackData("museumName", e.target.value)}
                    size="medium"
                />
            </div>

            <div className={styles.logoBlock}>
                <div className={stepStyles.fileUpload}>
                    <h2>{t("uploadYourMuseumLogo")}</h2>
                    <p>{t("dragYourLogoIntoTheAreaBelowOrSelectUploadLogo")}</p>
                    <ImageSpecsBadges spec="museumLogo" warnings={imageValidation.warnings} />
                    <FileInput
                        onFileSelected={handleFileSelected}
                        showButton={true}
                        acceptedFileTypes={IMAGE_FORMAT}
                        maxSize={IMAGE_MAX_SIZE_200_KB}
                        maxSizeLabel={t("imageMaxSize200kb")}
                    />
                </div>
            </div>

            <div className="photo-gallery">
                <ImageList
                    pictures={trackData && trackData.museumPictureUrl ? [trackData.museumPictureUrl] : []}
                    setPictures={setPicturesFromList}
                />
            </div>

            <div className={styles.descBlock}>
                <TextInput
                    placeholder={t("pleaseInsertAdditionalMuseumInformationHere")}
                    value={trackData.museumInfo || ""}
                    onChange={(e) => updateTrackData("museumInfo", e.target.value)}
                    size="big"
                    numberOfLines={6}
                />
            </div>

            <div className={styles.questionBlock}>
                <h2>{t("doesThisTourIncludeAVisualRepresentationOfTheMuseum")}</h2>
                <div className={styles.radioGroup}>
                    <RadioButton
                        label={t("no")}
                        subtitle={t(
                            "ifYouselectThisOptionTheUsersWillSeeTheListOfStoriesAvailableButWontHaveAccessToAMapOfTheMuseum",
                        )}
                        checked={trackData.isMuseumList === true}
                        onChange={() => updateTrackData("isMuseumList", true)}
                        activeImage={activeRadio}
                        inactiveImage={inactiveRadio}
                    />

                    <RadioButton
                        label={t("yes")}
                        subtitle={t("ifYouSelectThisOptionTheUsersWillSeeTheListOfStoriesAvailableOnAMapOfTheMuseum")}
                        checked={trackData.isMuseumList === false}
                        onChange={() => updateTrackData("isMuseumList", false)}
                        activeImage={activeRadio}
                        inactiveImage={inactiveRadio}
                    />
                </div>
            </div>

            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    label={t("continue")}
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                    onClick={navigateToNext}
                    disabled={!trackData.language || !trackData.type}
                />
            </div>
        </div>
    );
};

export default MuseumInfo;
